import React from 'react';
import { Col, Row, Typography, Spin, Empty, Card } from 'antd';
import { 
  FileTextOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useGetCommentsAndGapsSummary } from 'service/comment.service';
import { parseDpatGapsFromComment } from '../utils/dpatGapsHelpers';
import { isDpatQualityAssuranceCommentRole } from '../utils/dpatCommentRoleLabel';

const { Text } = Typography;

const gapsFromSummaryItem = (item) =>
  parseDpatGapsFromComment({
    gaps: item?.gap ?? item?.gaps,
    dddpData: item?.dddpData,
  });

const CommentAndGapsSummary = ({ district, year, region, enabled = true }) => {
  const { data: response, isLoading, isError, error } = useGetCommentsAndGapsSummary(
    district?.value,
    year,
    "DPAT",
    { enabled: enabled !== false },
  );

  // Backend already returns sorted and filtered data (CI → SDI → PI)
  const data = response?.data || { comments: [], gaps: [], summary: null };
  // Exclude QA comments — they have their own summary section
  const comments = (data.comments || []).filter(
    (item) => !isDpatQualityAssuranceCommentRole(item?.userRole),
  );
  const hasComments = comments.length > 0;
  const hasGaps = data.gaps?.length > 0;

  if (isLoading) {
    return (
      <Card>
        <div className="text-center py-12">
          <Spin size="large" tip="Loading assessment data..." />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div className="text-center py-12">
          <ExclamationCircleOutlined className="text-4xl text-red-500 mb-3" />
          <p className="text-muted-foreground">Error loading comments and gaps</p>
          <p className="text-xs text-muted-foreground mt-1">
            {error?.response?.data?.message || "Please try again later"}
          </p>
        </div>
      </Card>
    );
  }

  if (!hasComments && !hasGaps) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div className="text-center">
              <FileTextOutlined className="text-4xl text-muted-foreground mb-2" />
              <p>No comments or capacity gaps available</p>
              <p className="text-xs text-muted-foreground mt-1">
                No DPAT assessment data found for {district?.label} in {year}
              </p>
            </div>
          }
        />
      </Card>
    );
  }

  return (
    <div>
      {/* Header Info Row */}
      <Row className="py-2">
        <Col span={8} className="gutter-row">
          <Text strong>Name of MMDA: </Text> 
          <Text className="ms-3">{district?.label}</Text>
        </Col>
        <Col span={6} className="gutter-row">
          <Text strong>Region: </Text> 
          <Text>{region}</Text>
        </Col>
        <Col span={8} className="gutter-row">
          <Text strong>Date of Assessment: </Text> 
          <Text>{moment().format('MMMM Do YYYY, h:mm:ss A')}</Text>
        </Col>
      </Row>

      {/* Comments Table */}
      {hasComments && (
        <>
          <div className="table-responsive">
            <table className="table table-bordered" style={{
              border: '1px solid #000',
              borderCollapse: 'collapse',
              width: '100%',
              marginTop: "20px"
            }}>
              <thead>
                <tr style={{ fontWeight: 'bold', border: '1px solid #000' }}>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold', padding: '8px' }}>No.</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold', padding: '8px' }}>Indicator</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold', padding: '8px' }}>Comments</th>
                </tr>
              </thead>
              <tbody>
                {comments.map((item, index) => (
                  <tr key={index}>
                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                      <strong>{item.no}</strong>
                    </td>
                    <td style={{ border: '1px solid #000', padding: '8px' }}>
                      <strong>{item.indicator}</strong>
                    </td>
                    <td style={{ border: '1px solid #000', padding: '8px' }}>{item.comment}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <br />
          <hr />
          <div style={{ height: '4px', backgroundColor: '#000', width: '100%', margin: '20px 0' }} />
        </>
      )}

      {/* Capacity Gaps Table */}
      {hasGaps && (
        <div className="table-responsive">
          <h3 style={{ textAlign: "center", padding: "10px" }}>
            ANNEX 6 SUMMARY OF CAPACITY GAPS
          </h3>
          <table className="table table-bordered" style={{
            border: '1px solid #000',
            borderCollapse: 'collapse',
            width: '100%',
            marginTop: "20px"
          }}>
            <thead>
              <tr style={{ fontWeight: 'bold', border: '1px solid #000' }}>
                <th style={{ border: '1px solid #000', fontWeight: 'bold', padding: '8px' }}>No.</th>
                <th style={{ border: '1px solid #000', fontWeight: 'bold', padding: '8px' }}>Indicator</th>
                <th style={{ border: '1px solid #000', fontWeight: 'bold', padding: '8px' }}>Issues</th>
                <th style={{ border: '1px solid #000', fontWeight: 'bold', padding: '8px' }}>Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {data.gaps.map((item, index) => {
                const { issues, recommendation } = gapsFromSummaryItem(item);
                return (
                <tr key={index}>
                  <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                    <strong>{item.no}</strong>
                  </td>
                  <td style={{ border: '1px solid #000', padding: '8px' }}>
                    <strong>{item.indicator}</strong>
                  </td>
                  <td style={{ border: '1px solid #000', padding: '8px', whiteSpace: 'pre-wrap' }}>{issues}</td>
                  <td style={{ border: '1px solid #000', padding: '8px', whiteSpace: 'pre-wrap' }}>{recommendation}</td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CommentAndGapsSummary;